import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { toast } from 'sonner'
import { DatabaseZap, HardDrive, ServerCog, Sparkles, Trash2 } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { useAppActions } from '@/components/app/app-actions'
import { Button } from '@/components/ui/button'
import { SectionCard } from '@/components/app/common'
import { pluralize } from '@/lib/clinic/format'

export const DataSettingsPanel: FC = (): ReactNode => {
    const { data, loadSampleData, clearPatientRecords } = useClinic()
    const { confirm } = useAppActions()

    const sizeKb = useMemo(() => Math.max(1, Math.round(new Blob([JSON.stringify(data)]).size / 1024)), [data])

    const totals = [
        { label: 'Patients', value: data.patients.length },
        { label: 'Visits', value: data.visits.length },
        { label: 'Prescriptions', value: data.prescriptions.length },
        { label: 'Follow-ups', value: data.followUps.length },
        { label: 'Reminders logged', value: data.reminders.length }
    ]

    const askLoadSample = (): void => {
        confirm({
            title: 'Replace all records with sample data?',
            description: `This deletes the ${pluralize(data.patients.length, 'patient')} currently saved, with their visits, prescriptions and follow-ups, and loads 30 sample patients. Your settings are kept.`,
            confirmLabel: 'Load sample data',
            destructive: true,
            onConfirm: () => {
                loadSampleData()
                toast.success('Sample data loaded', { description: 'Dates are set relative to today.' })
            }
        })
    }

    const askClear = (): void => {
        confirm({
            title: 'Delete every patient record?',
            description: `All ${pluralize(data.patients.length, 'patient')}, ${pluralize(data.visits.length, 'visit')}, ${pluralize(data.prescriptions.length, 'prescription')} and ${pluralize(data.followUps.length, 'follow-up')} are permanently deleted from this browser. Clinic settings, templates and the treatment list are kept.`,
            confirmLabel: 'Delete all records',
            destructive: true,
            onConfirm: () => {
                clearPatientRecords()
                toast.success('All patient records deleted', { description: 'Patient IDs start again from DSC-1001.' })
            }
        })
    }

    return (
        <div className='grid gap-6'>
            <SectionCard icon={HardDrive} title='Saved records' description={`About ${sizeKb} KB used in this browser.`}>
                <dl className='grid grid-cols-2 gap-3 sm:grid-cols-5'>
                    {totals.map(item => (
                        <div key={item.label} className='rounded-xl border bg-muted/30 p-3'>
                            <dt className='text-xs font-medium text-muted-foreground'>{item.label}</dt>
                            <dd className='mt-1 font-heading text-2xl font-semibold text-foreground tabular-nums'>{item.value}</dd>
                        </div>
                    ))}
                </dl>

                <p className='mt-4 rounded-xl bg-amber-50 p-3 text-sm leading-relaxed text-amber-950'>
                    Records are saved in this browser on this device. Use the same computer and browser at the front desk, and avoid clearing browsing data, or the records will be lost.
                </p>
            </SectionCard>

            <SectionCard icon={Sparkles} title='Sample data' description={data.meta.sample ? 'You are currently using sample patients.' : 'Load sample patients to explore or train staff.'}>
                <div className='flex flex-wrap items-center justify-between gap-3'>
                    <p className='max-w-xl text-sm text-muted-foreground'>
                        30 sample patients with visits, prescriptions, upcoming, overdue and completed follow-ups. Dates are generated relative to today.
                    </p>

                    <Button type='button' variant='outline' onClick={askLoadSample}>
                        <DatabaseZap />
                        Load sample data
                    </Button>
                </div>
            </SectionCard>

            <SectionCard icon={Trash2} title='Start with real records' description='Remove every patient, visit, prescription and follow-up.' className='border-rose-200'>
                <div className='flex flex-wrap items-center justify-between gap-3'>
                    <p className='max-w-xl text-sm text-muted-foreground'>
                        Use this once you are ready to register real patients. Clinic information, message templates and treatments stay as they are.
                    </p>

                    <Button type='button' variant='destructive' onClick={askClear} disabled={data.patients.length === 0}>
                        <Trash2 />
                        Delete all records
                    </Button>
                </div>
            </SectionCard>

            <SectionCard icon={ServerCog} title='Ready for a secure server' description='For later, when the clinic grows.'>
                <p className='text-sm leading-relaxed text-muted-foreground'>
                    The system is built so the records can move to a secure online database, shared by the doctor and the reception on several devices, and so WhatsApp reminders can be sent automatically on a schedule. Until then, everything works on this device.
                </p>
            </SectionCard>
        </div>
    )
}
